import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import './LanguageSwitcher.css';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'tr', label: 'TR' },
  { code: 'de', label: 'DE' },
  { code: 'es', label: 'ES' },
  { code: 'fr', label: 'FR' },
] as const;

type LanguageCode = (typeof LANGUAGES)[number]['code'];

function resolveActiveCode(language: string): LanguageCode {
  const base = (language || 'en').split('-')[0];
  const match = LANGUAGES.find((lang) => lang.code === base);
  return match ? match.code : 'en';
}

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const activeCode = resolveActiveCode(i18n.language);
  const activeLanguage = LANGUAGES.find((lang) => lang.code === activeCode) ?? LANGUAGES[0];
  const otherLanguages = LANGUAGES.filter((lang) => lang.code !== activeCode);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleSelect = (code: LanguageCode) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={`language-switcher ${open ? 'is-open' : ''}`}
      role="navigation"
      aria-label="Language"
    >
      <button
        type="button"
        className="language-switcher-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{activeLanguage.label}</span>
        <span className="language-switcher-caret" aria-hidden="true" />
      </button>

      <div className="language-switcher-menu" role="listbox" aria-hidden={!open}>
        {otherLanguages.map(({ code, label }) => (
          <button
            key={code}
            type="button"
            role="option"
            className="language-switcher-option"
            tabIndex={open ? 0 : -1}
            onClick={() => handleSelect(code)}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
